import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, Clock, RotateCcw, XCircle } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { API } from "../lib/eventUtils";

const MAX_ATTEMPTS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [result, setResult] = useState({ phase: "checking" });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setResult({ phase: "failed" });
      return undefined;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (data.kind === "event_registration") {
            if (data.fulfillment_status === "fulfilled") {
              setResult({ phase: "success", data });
            } else if ((data.fulfillment_status || "").startsWith("refunded")) {
              setResult({ phase: "refunded", data });
            } else if (attempts < MAX_ATTEMPTS) {
              attempts += 1;
              timerRef.current = setTimeout(poll, 2000);
            } else {
              setResult({ phase: "timeout", data });
            }
          } else {
            setResult({ phase: "success", data });
          }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setResult({ phase: "failed" });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setResult({ phase: "timeout", data });
          return;
        }
        attempts += 1;
        timerRef.current = setTimeout(poll, 2000);
      } catch {
        setResult({ phase: "failed" });
      }
    };
    poll();
    return () => clearTimeout(timerRef.current);
  }, [sessionId]);

  const { phase, data } = result;
  const isEvent = data?.kind === "event_registration";
  const eventLink = data?.event ? `/events/${data.event.slug}` : "/events";

  return (
    <div data-testid="payment-success-page" className="pt-16 min-h-[70vh] flex items-center justify-center px-5">
      <Seo title="Payment — HyroxNinja" description="Payment confirmation for HyroxNinja events and support." noindex />
      <div className="border border-white/10 bg-[#0c0c0c] p-8 sm:p-12 max-w-lg w-full text-center">
        {phase === "checking" && (
          <div data-testid="payment-checking">
            <Clock size={44} className="text-volt mx-auto mb-5 animate-pulse" />
            <h1 className="font-head font-extrabold uppercase text-3xl mb-3">Confirming payment…</h1>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Hold tight — we're confirming your payment with Stripe. This usually takes a few seconds.
            </p>
          </div>
        )}
        {phase === "success" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 size={48} className="text-volt mx-auto mb-5" />
            <h1 className="font-head font-extrabold uppercase text-3xl mb-3">
              {isEvent ? "You're in!" : "Thank you!"}
            </h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-7">
              {isEvent ? (
                <>Payment confirmed — your spot for <span className="text-white font-semibold">{data.event?.title}</span> is locked in. See you there.</>
              ) : (
                <>Your support keeps the HyroxNinja program running — sessions, simulations and the Dallas HYROX community. We appreciate you.</>
              )}
            </p>
            <Link to={isEvent ? eventLink : "/"} className="btn-volt justify-center" data-testid="payment-success-cta">
              {isEvent ? "Back to the Event" : "Back Home"} <ArrowRight size={16} />
            </Link>
          </div>
        )}
        {phase === "refunded" && (
          <div data-testid="payment-refunded">
            <RotateCcw size={48} className="text-amber-400 mx-auto mb-5" />
            <h1 className="font-head font-extrabold uppercase text-3xl mb-3">Spot unavailable — refunded</h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-7">
              {data?.fulfillment_status === "refunded_duplicate"
                ? "This email is already registered for the event, so we refunded this payment in full."
                : "The event filled up before your payment completed, so we refunded you in full."}{" "}
              The refund lands back on your card within a few business days.
            </p>
            <Link to={eventLink} className="btn-ghost justify-center" data-testid="payment-refunded-cta">
              View the Event <ArrowRight size={16} />
            </Link>
          </div>
        )}
        {phase === "timeout" && (
          <div data-testid="payment-timeout">
            <Clock size={48} className="text-amber-400 mx-auto mb-5" />
            <h1 className="font-head font-extrabold uppercase text-3xl mb-3">Still processing</h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-7">
              Your payment went through but confirmation is taking longer than usual. Refresh this page in a
              minute — your spot is being finalized.
            </p>
            <button onClick={() => window.location.reload()} className="btn-volt justify-center" data-testid="payment-timeout-refresh">
              Refresh <RotateCcw size={15} />
            </button>
          </div>
        )}
        {phase === "failed" && (
          <div data-testid="payment-failed">
            <XCircle size={48} className="text-red-400 mx-auto mb-5" />
            <h1 className="font-head font-extrabold uppercase text-3xl mb-3">Payment not completed</h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-7">
              We couldn't confirm this payment — the checkout may have expired or failed. No charge was made.
            </p>
            <Link to="/events" className="btn-volt justify-center" data-testid="payment-failed-cta">
              Back to Events <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
